import {
    IconArrowRight,
    IconSettings,
    IconShare,
    IconSpaces,
    IconTag,
    IconUsers,
    IconUsersGroup,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { Button, Card, StatusChip, Text } from '@fragments';
import { CommunityBanner } from '@components/Community';

import './styles.scss';

// `label` is a translation key — resolved at render so it follows a language change.
const STATUS = {
    published: { Chip: StatusChip.Success, label: 'home.hero.statusPublished' },
    draft: { Chip: StatusChip.Default, label: 'common:status.draft' },
};

/**
 * CommunityHeroCard — identity card of the active owner community: the
 * mask-faded condensed hero touching the top corners, then avatar + name +
 * status and three counters (members / plans / groups), with the owner
 * actions in the footer: settings + share side by side over a full-width
 * "Abrir". `split` hands body and footer to the owner bento as two separate
 * blocks (see Card); the bento sets their corner radii.
 *
 * `plansCount` comes from the overview batch; `stats.groups` from the shared
 * per-community stats map. Both render "0" when absent, and shimmer while the
 * batch is still in flight.
 */
export default function CommunityHeroCard({
    community,
    plansCount,
    stats,
    loading,
    onOpen,
    onSettings,
    onShare,
    split = false,
}) {
    const { t } = useTranslation('dashboard');

    if (!community) return null;

    const status = STATUS[community.status] || STATUS.draft;
    const hero = community.hero_condensed || community.hero;

    const counters = [
        { label: t('common:entities.members'), Icon: IconUsers, value: community.members?.total ?? 0, loading: false },
        { label: t('common:entities.plans'), Icon: IconTag, value: plansCount ?? 0, loading },
        { label: t('common:entities.groups'), Icon: IconUsersGroup, value: stats?.groups ?? 0, loading: !stats },
    ];

    return (
        <Card
            flat={!split}
            split={split}
            noBodyPadding
            className="community-hero"
            footer={
                <div className="community-hero__actions">
                    <div className="community-hero__actions-row">
                        <Button
                            color="overlay"
                            size="small"
                            fullWidth
                            startIcon={<IconSettings />}
                            onClick={() => onSettings(community)}
                        >
                            {t('home.hero.settings')}
                        </Button>
                        <Button
                            color="overlay"
                            size="small"
                            fullWidth
                            startIcon={<IconShare />}
                            onClick={() => onShare(community)}
                        >
                            {t('common:actions.share')}
                        </Button>
                    </div>
                    <Button
                        color="inverse"
                        size="medium"
                        fullWidth
                        endIcon={<IconArrowRight />}
                        onClick={() => onOpen(community)}
                    >
                        {t('home.hero.open')}
                    </Button>
                </div>
            }
        >
            {/* Condensed hero (6:4), flush to the card's top corners, dissolving
                toward the bottom through an alpha mask. */}
            <div className="community-hero__media" aria-hidden="true">
                {hero ? <img src={hero} alt="" /> : <IconSpaces />}
            </div>

            {/* Content over the dissolved zone. */}
            <div className="community-hero__content">
                <div className="community-hero__identity">
                    <CommunityBanner banner={community.banner} size={44} className="community-hero__logo" />
                    <div className="community-hero__name">
                        <Text.h5 weight={500} contrast={10} maxLines={1}>{community.name}</Text.h5>
                        <Text.caption contrast={6}>/{community.slug}</Text.caption>
                    </div>
                    <status.Chip text={t(status.label)} />
                </div>

                <dl className="community-hero__stats">
                    {counters.map(({ label, Icon, value, loading: itemLoading }) => (
                        <div key={label} className="community-hero__stat">
                            <dt>
                                <Text.caption contrast={6} left={<Icon />}>{label}</Text.caption>
                            </dt>
                            <dd>
                                <Text.h4 weight={500} contrast={10} loading={itemLoading}>
                                    {String(value)}
                                </Text.h4>
                            </dd>
                        </div>
                    ))}
                </dl>
            </div>
        </Card>
    );
}
